import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Alert, Typography, Space, Card } from 'antd';
import { MailOutlined, ReloadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useAuthStore } from '../stores/authStore';
import { THEME_COLORS, FEEDBACK_BACKGROUND_COLORS } from '@/shared/constants/colors';

const { Text, Title } = Typography;

/** 認証コード桁数 */
const CODE_LENGTH = 6;

/** 再送信クールダウン秒数 */
const RESEND_COOLDOWN_SECONDS = 60;

/** 秒からミリ秒への変換定数 */
const SECONDS_TO_MILLISECONDS = 1000;

/** UI定数 */
const UI_CONSTANTS = {
  TITLE_LEVEL: 3,
  ICON_SIZE: 48,
  MAX_FORM_WIDTH: 400,
  PADDING: 24,
  MARGIN_BOTTOM_LARGE: 24,
  MARGIN_BOTTOM_MEDIUM: 16,
  BORDER_RADIUS: 4,
  INPUT_LETTER_SPACING: 8,
  INPUT_FONT_SIZE: 24,
} as const;

/** 2FAフォーム入力値 */
interface TwoFactorFormValues {
  readonly code: string;
}

export const TwoFactorForm: React.FC = () => {
  const {
    verifyTwoFactor,
    resendTwoFactorCode,
    cancelTwoFactor,
    maskedEmail,
    twoFactorExpiresAt,
    debugCode,
    isLoading,
    error,
    clearError,
  } = useAuthStore();

  const [form] = Form.useForm<TwoFactorFormValues>();
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [resendCooldown, setResendCooldown] = useState<number>(0);

  // 有効期限カウントダウン
  useEffect(() => {
    if (twoFactorExpiresAt === null) {
      return;
    }

    const updateTimer = (): void => {
      const now = Date.now();
      const expiry = new Date(twoFactorExpiresAt).getTime();
      const remaining = Math.max(0, Math.floor((expiry - now) / SECONDS_TO_MILLISECONDS));
      setRemainingTime(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, SECONDS_TO_MILLISECONDS);

    return () => {
      clearInterval(interval);
    };
  }, [twoFactorExpiresAt]);

  // 再送信クールダウン
  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }

    const timer = setTimeout(() => {
      setResendCooldown((prev) => prev - 1);
    }, SECONDS_TO_MILLISECONDS);

    return () => {
      clearTimeout(timer);
    };
  }, [resendCooldown]);

  const handleSubmit = useCallback(
    async (values: TwoFactorFormValues): Promise<void> => {
      try {
        await verifyTwoFactor(values.code);
      } catch {
        // エラーはstoreで処理済み
      }
    },
    [verifyTwoFactor]
  );

  const handleResend = useCallback(async (): Promise<void> => {
    try {
      await resendTwoFactorCode();
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      // エラーはstoreで処理済み
    }
  }, [resendTwoFactorCode]);

  const handleCancel = useCallback((): void => {
    cancelTwoFactor();
  }, [cancelTwoFactor]);

  const formatTime = (seconds: number): string => {
    const SECONDS_PER_MINUTE = 60;
    const mins = Math.floor(seconds / SECONDS_PER_MINUTE);
    const secs = seconds % SECONDS_PER_MINUTE;
    const PADDING_LENGTH = 2;
    return `${mins}:${secs.toString().padStart(PADDING_LENGTH, '0')}`;
  };

  const isExpired = remainingTime === 0;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <Card className="w-full max-w-md shadow-lg">
        <div style={{ maxWidth: UI_CONSTANTS.MAX_FORM_WIDTH, margin: '0 auto' }}>
          <Title level={UI_CONSTANTS.TITLE_LEVEL} style={{ textAlign: 'center', marginBottom: 8 }}>
            認証コードを入力
          </Title>

          <div style={{ textAlign: 'center', marginBottom: UI_CONSTANTS.MARGIN_BOTTOM_LARGE }}>
            <MailOutlined
              style={{
                fontSize: UI_CONSTANTS.ICON_SIZE,
                color: THEME_COLORS.PRIMARY,
                marginBottom: UI_CONSTANTS.MARGIN_BOTTOM_MEDIUM,
              }}
            />
            <br />
            <Text type="secondary">
              {maskedEmail} に送信された6桁のコードを入力してください。
            </Text>
          </div>

          <div
            style={{
              textAlign: 'center',
              marginBottom: UI_CONSTANTS.MARGIN_BOTTOM_MEDIUM,
              padding: '8px 16px',
              background: isExpired ? FEEDBACK_BACKGROUND_COLORS.ERROR_LIGHT : FEEDBACK_BACKGROUND_COLORS.SUCCESS_LIGHT,
              borderRadius: UI_CONSTANTS.BORDER_RADIUS,
            }}
          >
            <Text type={isExpired ? 'danger' : 'success'}>
              {isExpired ? 'コードの有効期限が切れました' : `残り時間: ${formatTime(remainingTime)}`}
            </Text>
          </div>

          {error !== null && (
            <Alert
              message={error}
              type="error"
              closable
              onClose={clearError}
              style={{ marginBottom: UI_CONSTANTS.MARGIN_BOTTOM_MEDIUM }}
            />
          )}

          {debugCode !== null && (
            <Alert
              message="STG環境デバッグ用"
              description={`認証コード: ${debugCode}`}
              type="info"
              style={{ marginBottom: UI_CONSTANTS.MARGIN_BOTTOM_MEDIUM }}
            />
          )}

          <Form form={form} onFinish={handleSubmit} layout="vertical">
            <Form.Item
              name="code"
              rules={[
                { required: true, message: '認証コードを入力してください' },
                { len: CODE_LENGTH, message: `${CODE_LENGTH}桁のコードを入力してください` },
                { pattern: /^\d+$/, message: '数字のみ入力してください' },
              ]}
            >
              <Input
                size="large"
                placeholder="000000"
                maxLength={CODE_LENGTH}
                style={{
                  textAlign: 'center',
                  letterSpacing: UI_CONSTANTS.INPUT_LETTER_SPACING,
                  fontSize: UI_CONSTANTS.INPUT_FONT_SIZE,
                  fontFamily: 'monospace',
                }}
                autoFocus
                disabled={isExpired}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                size="large"
                disabled={isExpired}
              >
                確認
              </Button>
            </Form.Item>
          </Form>

          <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }}>
            <Button
              type="link"
              icon={<ReloadOutlined />}
              onClick={handleResend}
              disabled={resendCooldown > 0 || isLoading}
            >
              {resendCooldown > 0 ? `コードを再送信 (${resendCooldown}秒後)` : 'コードを再送信'}
            </Button>

            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={handleCancel}
              disabled={isLoading}
            >
              ログイン画面に戻る
            </Button>
          </Space>
        </div>
      </Card>
    </div>
  );
};
